import axios from 'axios';
import { telegram } from '../utils/telegram';
import { logger } from '../utils/logger';
import { storageGet, storageSet, storageRemove } from '../utils/storage';

/**
 * Axios instance for API calls.
 *
 * Features:
 * - Automatic JWT token injection
 * - Automatic Telegram initData injection (for tenant resolution)
 * - 401 handling with automatic token refresh (rotation)
 */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

async function clearTokensAndNotify(): Promise<void> {
  await Promise.all([
    storageRemove('accessToken'),
    storageRemove('refreshToken'),
  ]);
  logger.warn('Auth failed — tokens cleared, dispatching auth:logout event');
  window.dispatchEvent(new CustomEvent('auth:logout'));
}

/**
 * Request interceptor: Add auth headers
 */
apiClient.interceptors.request.use(
  (config) => {
    // 1. Add JWT token if exists (per-request Authorization override qilinmagan bo'lsa)
    const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 2. Add Telegram initData for tenant resolution
    const initData = telegram.getInitData();
    if (initData) {
      config.headers['X-Telegram-Init-Data'] = initData;
    } else {
      logger.warn('No initData available - API calls may fail', {
        method: config.method?.toUpperCase(),
        url: config.url,
      });
    }

    // 3. Add Accept-Language for i18n support
    const lang = localStorage.getItem('preferredLanguage') ?? 'uz';
    config.headers['Accept-Language'] = lang;

    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isFormData) {
      delete config.headers['Content-Type'];
    }
    logger.debug('API Request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      params: config.params ?? null,
      body: isFormData
        ? '[FormData — omitted]'
        : config.data
          ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data)
          : null,
      headers: {
        hasAuth: !!token,
        hasInitData: !!initData,
        idempotencyKey: (config.headers['Idempotency-Key'] as string | undefined) ?? null,
      },
    });

    return config;
  },
  (error) => {
    logger.error('API Request error', { error: error.message });
    return Promise.reject(error);
  }
);

/**
 * Response interceptor: Handle errors and auto-refresh tokens
 *
 * 401 handling by error code:
 * - JWT_EXPIRED     → try /refresh → retry original request
 * - JWT_INVALID     → try /refresh → if fails → auth:logout
 * - TENANT_CONTEXT_LOST → auth:logout immediately (no refresh attempt)
 */
apiClient.interceptors.response.use(
  (response) => {
    const isAuthEndpoint = response.config.url?.includes('/auth/');
    logger.debug('API Response', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      data: isAuthEndpoint ? '[auth — hidden]' : response.data,
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    logger.error('API Response error', {
      method: originalRequest?.method?.toUpperCase(),
      url: originalRequest?.url,
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
    });

    if (error.response?.status === 403) {
      const code = error.response?.data?.code as string | undefined;
      if (code === 'PROVIDER_BLOCKED_BY_OWNER') {
        window.dispatchEvent(new CustomEvent('provider:blocked'));
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const code = error.response?.data?.code as string | undefined;

      // TENANT_CONTEXT_LOST → no refresh attempt, re-bootstrap immediately
      if (code === 'TENANT_CONTEXT_LOST') {
        await clearTokensAndNotify();
        return Promise.reject(error);
      }

      // JWT_EXPIRED or JWT_INVALID → try refresh
      const storedRefresh = await storageGet('refreshToken');

      if (storedRefresh) {
        try {
          logger.info('AccessToken expired, attempting refresh...');

          const res = await axios.post(
            `${import.meta.env.VITE_API_BASE_URL}/public/auth/refresh`,
            { refreshToken: storedRefresh }
          );

          const { accessToken, refreshToken: newRefreshToken } = res.data.data;

          await Promise.all([
            storageSet('accessToken', accessToken),
            storageSet('refreshToken', newRefreshToken),
          ]);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          logger.info('Token refreshed successfully');
          return apiClient(originalRequest);
        } catch (refreshError) {
          logger.error('Token refresh failed', { error: refreshError });
        }
      }

      // Refresh failed or no token.
      // For /me/session: bootstrap handles re-auth via telegram-login — skip auth:logout dispatch.
      // For all other endpoints: clear tokens and notify App to reset to error state.
      if (!originalRequest.url?.includes('/me/session')) {
        await clearTokensAndNotify();
      } else {
        await Promise.all([
          storageRemove('accessToken'),
          storageRemove('refreshToken'),
        ]);
        logger.warn('/me/session auth failed — tokens cleared, bootstrap will handle re-auth');
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
